import React from 'react';
import PropTypes from 'prop-types';
import {
  Heading_1 as Heading1,
  useThemeContext
} from '@huggg/react-components';

import styles from './segmentCarouselDisplay.module.scss';
import SegmentCardDisplay from './SegmentCardDisplay';
import classNames from 'classnames';
import {
  SecondaryButton,
  ButtonArrowState,
  FontFamily
} from '@huggg/react-components';

// pixels-per-animation-frame
const SCROLL_RATE = 20;

const SVGArrow = ({ points }) => (
  <svg
    width="24px"
    height="24px"
    viewBox="0 0 24 24"
    strokeWidth="2"
    stroke="currentColor"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <polyline points={points} />
  </svg>
);
SVGArrow.propTypes = { points: PropTypes.string.isRequired };

export const SegmentCarouselDisplay = props => {
  const { huggg, segments, onSelectSegment } = props;
  const carouselRef = React.useRef(null);
  const scrolling = React.useRef(null);
  const observer = React.useRef(null);
  const [currentSegments, setCurrentSegments] = React.useState(segments || []);
  const [segmentHistory, setSegmentHistory] = React.useState([]);

  const [overflow, setOverflow] = React.useState(0);

  const { name: theme } = useThemeContext();

  const redeemMessage = onSelectSegment
    ? theme === 'welfare'
      ? 'Please select the voucher you would like to redeem.'
      : 'Take your pick:'
    : null;

  // Update currentSegments when segments prop changes
  React.useEffect(() => {
    setCurrentSegments(segments || []);
  }, [segments]);

  const handleSegmentClick = segment => {
    if (!onSelectSegment) return;

    if (segment.itemType === 'segment-group' && segment.items) {
      setSegmentHistory(prev => [...prev, currentSegments]);
      setCurrentSegments(segment.items);
      if (carouselRef.current) {
        carouselRef.current.scrollLeft = 0;
      }

      window.scrollTo(0, 0);
    } else {
      const isNested = segmentHistory.length > 0;
      onSelectSegment(segment, isNested);
    }
  };

  const handleBackClick = () => {
    if (segmentHistory.length > 0) {
      const previousSegments = segmentHistory[segmentHistory.length - 1];
      setCurrentSegments(previousSegments);
      setSegmentHistory(prev => prev.slice(0, -1));
      if (carouselRef.current) {
        carouselRef.current.scrollLeft = 0;
      }
    }
  };

  const doScroll = modify => {
    const change = modify(carouselRef.current.scrollLeft, SCROLL_RATE);

    if (change > overflow || change < 0) {
      return;
    }

    carouselRef.current.scrollLeft = change;
    scrolling.current = window.requestAnimationFrame(() => doScroll(modify));
  };

  const scrollLeft = () => {
    doScroll((a, b) => a - b);
  };

  const scrollRight = () => {
    doScroll((a, b) => a + b);
  };

  const stopScroll = () => {
    window.cancelAnimationFrame(scrolling.current);
  };

  const calculateOverflow = (currentRef, setOverflow) => {
    const { scrollWidth, offsetWidth } = currentRef;

    setOverflow(scrollWidth - offsetWidth);
  };

  React.useEffect(() => {
    if (!carouselRef.current) {
      return;
    }

    calculateOverflow(carouselRef.current, setOverflow);

    observer.current = new ResizeObserver(() =>
      calculateOverflow(carouselRef.current, setOverflow)
    );
    observer.current.observe(carouselRef.current);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      observer.current.unobserve(carouselRef.current);
      window.cancelAnimationFrame(scrolling.current);
    };
  }, [currentSegments]);

  // Don't render if no segments
  if (!currentSegments || currentSegments.length === 0) {
    return null;
  }

  return (
    <>
      {redeemMessage && (
        <div className={styles.redeemMessage}>
          {segmentHistory.length > 0 && (
            <SecondaryButton
              onClick={handleBackClick}
              data-test-id="segment-back-button"
              className={styles.backButton}
              label="Back"
              arrow={ButtonArrowState.Backwards}
              style={{
                fontWeight: '300'
              }}
              fontFamily={FontFamily.PoppinsRegular}
            />
          )}
          <Heading1>{redeemMessage}</Heading1>
        </div>
      )}
      <div className={styles.segments} data-test-id="segment-options">
        <div className={styles.segmentWrapper}>
          <div
            data-test-id="segment-carousel"
            className={styles.segmentCarousel}
            ref={carouselRef}
          >
            {currentSegments.map((segment, index) => (
              <div
                key={segment.id}
                data-test-id={`segment-${index}`}
                style={{ width: '100%', height: '100%' }}
              >
                <SegmentCardDisplay
                  data-test-id={`segment-card-${index}`}
                  segment={segment}
                  huggg={huggg}
                  shadow={true}
                  chooseSegment={
                    onSelectSegment ? () => handleSegmentClick(segment) : null
                  }
                />
              </div>
            ))}
            {overflow ? (
              <>
                <div
                  onTouchStart={scrollLeft}
                  onMouseDown={scrollLeft}
                  onTouchEnd={stopScroll}
                  onMouseUp={stopScroll}
                  onMouseLeave={stopScroll}
                  className={classNames(
                    styles.carouselArrow,
                    styles.carouselArrowPrev
                  )}
                >
                  <SVGArrow points="15 6 9 12 15 18" />
                </div>
                <div
                  onTouchStart={scrollRight}
                  onMouseDown={scrollRight}
                  onTouchEnd={stopScroll}
                  onMouseUp={stopScroll}
                  onMouseLeave={stopScroll}
                  className={classNames(
                    styles.carouselArrow,
                    styles.carouselArrowNext
                  )}
                >
                  <SVGArrow points="9 6 15 12 9 18" />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

SegmentCarouselDisplay.propTypes = {
  huggg: PropTypes.object,
  segments: PropTypes.array,
  onSelectSegment: PropTypes.func
};

export default SegmentCarouselDisplay;
